import getHandlerAndLogAction from './getHandlerAndLogAction';
import getReadStaticActionsMap from './getReadStaticActionsMap';
import isGroupItem from './isGroupItem';
import { CommandBarItem } from './schema/CommandBarItem';
import { ItemActionsProps } from './schema/ItemActionsProps';
import { updateMenuItem } from './updateMenuItem';
import { ReadActionKey } from 'owa-outlook-service-options';
import {
    getReadActionDisplayName,
    getReadActionIconName,
    SurfaceActionItem,
    ReadStaticAction,
} from 'owa-surface-actions';

const styles = require('../components/ItemActions.scss');

export default function createStaticActionCommandBarItem(
    action: ReadStaticAction,
    props: ItemActionsProps
): SurfaceActionItem {
    if (
        !action ||
        (action.hideInGroup && isGroupItem(props.item)) ||
        (action.hideInSxS && props.isItemAttachment)
    ) {
        return null;
    }

    const commandBarItem = initializeMenuItem(action.key as ReadActionKey);
    updateMenuItem[action.key](props, commandBarItem);
    const submenuItems = getSubmenuItems(commandBarItem.subMenuActionKeys, props);
    // If the action has sub menu items, but they are all hidden in group or sxs,
    // submenuItems gets null, and we do not want to show the parent action either.
    // For example, if all submenu items in Other reply actions are hidden in group,
    // we  do not want to show 'Other reply actions' either.
    if (commandBarItem.subMenuActionKeys && !submenuItems) {
        return null;
    }

    const surfaceActionItem = createSurfaceActionItem(commandBarItem, submenuItems, props);
    return surfaceActionItem;
}

function initializeMenuItem(key: ReadActionKey): CommandBarItem {
    const displayName = getReadActionDisplayName(key);

    return {
        key: key,
        displayName: displayName,
        iconName: getReadActionIconName(key)?.iconName,
        customRender: null,
        isDisabled: false,
        clickHandler: null,
        subMenuActionKeys: null,
        subMenuProps: null,
        datapointName: key as string,
        hideInOverflow: false,
    };
}

function createSurfaceActionItem(
    menuItem: CommandBarItem,
    items: SurfaceActionItem[],
    props: ItemActionsProps
): SurfaceActionItem {
    if (!menuItem.clickHandler) {
        return null;
    }

    return {
        key: menuItem.key,
        text: menuItem.displayName,
        iconProps: {
            iconName: menuItem.iconName,
            className: styles.commandBarIcon,
        },
        onRender: menuItem.customRender,
        disabled: menuItem.isDisabled,
        onClick: getHandlerAndLogAction(
            menuItem.clickHandler,
            true /*isMouse*/,
            menuItem.datapointName,
            props.viewState,
            props.actionSource
        ),
        onKeyDown: getHandlerAndLogAction(
            menuItem.clickHandler,
            false /*isMouse*/,
            menuItem.datapointName,
            props.viewState,
            props.actionSource
        ),
        iconOnly: true,
        ariaLabel: menuItem.displayName,
        subMenuProps: menuItem.subMenuProps
            ? menuItem.subMenuProps
            : items
            ? {
                  items: items,
              }
            : null,
        hideInOverflow: menuItem.hideInOverflow,
    };
}

function getSubmenuItems(
    subMenuActionKeys: ReadActionKey[],
    props: ItemActionsProps
): SurfaceActionItem[] {
    if (!subMenuActionKeys) {
        return null;
    }

    const readStaticActionsMap = getReadStaticActionsMap();
    const items = [];
    subMenuActionKeys.forEach(propKey => {
        const action = readStaticActionsMap[propKey];
        const subMenuItem = createStaticActionCommandBarItem(action, props);
        !!subMenuItem && items.push(subMenuItem);
    });
    return items.length > 0 ? items : null;
}
